import { HttpErrorResponse } from '@angular/common/http';
import { ToastSeverity } from '../../shared/enums/toast-severity.enum';
import { MessageService } from 'primeng/api';
import { inject, Injectable } from '@angular/core';
import {
  ErrorCode,
  ErrorCodeDescriptions,
} from '../../shared/enums/error-code.enum';

export interface HttpErrorDetails {
  message?: string;
  errorCode?: ErrorCode;
  timestamp?: string;
  token?: string;
  userId?: number;
  hostAddress?: string;
}

export enum HttpResponseErrorSource {
  MESSAGE_FROM_FUNCTION,
  MESSAGE_FROM_RESPONSE,
  MESSAGE_FROM_ERROR_CODE,
}

@Injectable({ providedIn: 'root' })
export class HttpResponseService {
  #messageService = inject(MessageService);
  readonly #DOT_REMOVE_REGEX = /[.。]\s*$/;

  /**
   * Wyświetla komunikat błędu w oknie PrimeNG Toast na podstawie analizy odpowiedzi HTTP.
   *
   * @param error `HttpErrorResponse` - obiekt błędu zwrócony przez Angular HttpClient
   * @param errorMessage `string` - domyślny komunikat błędu (używany gdy brak danych z serwera) lub gdy errorSource = MESSAGE_FROM_FUNCTION (domyślnie)
   * @param errorSource `HttpResponseErrorSource` - źródło komunikatu:
   *   - `MESSAGE_FROM_FUNCTION` - używa `errorMessage` (domyślnie)
   *   - `MESSAGE_FROM_RESPONSE` - używa `errorDetails.message` z odpowiedzi API
   *   - `MESSAGE_FROM_ERROR_CODE` - używa opisu z `ErrorCodeDescriptions[errorCode]`
   *
   * @returns `void`
   *
   * **Uwaga:** Jeśli serwer zwróci token błędu (`errorDetails.token`), zostanie automatycznie dołączony do komunikatu.
   * W przypadku nierozpoznawalnych danych błędu (np. brak odpowiedzi serwera) wyświetli się ogólny komunikat: "Wystąpił błąd serwera..."
   */
  showErrorMessage(
    error: HttpErrorResponse,
    errorMessage: string,
    errorSource: HttpResponseErrorSource = HttpResponseErrorSource.MESSAGE_FROM_FUNCTION,
  ): void {
    let errorDetails: HttpErrorDetails | undefined = this.#parseErrorDetails(
      error.error,
    );

    if (errorDetails) {
      this.#showError(errorDetails, errorMessage, errorSource);
    } else {
      this.#messageService.add({
        severity: ToastSeverity.ERROR,
        sticky: true,
        summary:
          'Wystąpił błąd podczas pobieranie niezbędnych danych. Spróbuj ponownie za kilka minut.',
      });
    }
  }

  showSuccessMessage(errorMessage: string): void {
    const message = this.#prepareMessage(errorMessage);
    this.#messageService.add({
      severity: ToastSeverity.SUCCESS,
      sticky: false,
      summary: `${message}.`,
    });
  }

  #showError(
    errorDetails: HttpErrorDetails,
    errorMessage: string,
    errorSource: HttpResponseErrorSource,
  ): void {
    let message;
    switch (errorSource) {
      case HttpResponseErrorSource.MESSAGE_FROM_FUNCTION:
        message = this.#prepareMessage(errorMessage);
        break;
      case HttpResponseErrorSource.MESSAGE_FROM_RESPONSE:
        message = errorDetails?.message
          ? this.#prepareMessage(errorDetails.message)
          : this.#prepareMessage(errorMessage);
        break;
      case HttpResponseErrorSource.MESSAGE_FROM_ERROR_CODE:
        const customErrorCode = errorDetails.errorCode;
        message =
          customErrorCode && ErrorCodeDescriptions[customErrorCode]
            ? this.#prepareMessage(ErrorCodeDescriptions[customErrorCode])
            : this.#prepareMessage(errorMessage);
        break;
    }

    const errorText = errorDetails.token
      ? `${message}. Unikalny identyfikator błędu: [${errorDetails.token}]`
      : `${message}.`;

    this.#messageService.add({
      severity: ToastSeverity.ERROR,
      sticky: true,
      summary: errorText,
    });
  }

  #parseErrorDetails(error: any): HttpErrorDetails | undefined {
    if (typeof error === 'object')
      return this.#isErrorDetails(error) ? error : undefined;

    if (typeof error === 'string') {
      try {
        const parsed = JSON.parse(error);
        return this.#isErrorDetails(parsed) ? parsed : undefined;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  #prepareMessage(msg: string): string {
    return msg.replace(this.#DOT_REMOVE_REGEX, '').trim();
  }

  #isErrorDetails(error: any): error is HttpErrorDetails {
    return (
      error &&
      typeof error === 'object' &&
      ('errorCode' in error || 'message' in error || 'timestamp' in error)
    );
  }
}
